import { create } from 'zustand'
import type { Product, Category, Selection, DecisionTree, FunnelSize, ConfigStep, QuestionStep } from '@/types'
import { useProductStore } from './products'

interface ConfiguratorStore {
  // Data
  funnelId: string | null
  size: FunnelSize
  categories: Category[]
  configSteps: ConfigStep[]
  decisionTree: DecisionTree | null
  baseProductId: string | null
  baseImage: string | null

  // State
  selections: Selection[]
  questionAnswers: Record<string, 'yes' | 'no'> // questionId -> answer

  // Actions
  init: (data: {
    funnelId: string
    size: FunnelSize
    categories: Category[]
    configSteps: ConfigStep[]
    decisionTree?: DecisionTree
    baseProductId: string
    baseImage?: string
  }) => void
  selectProduct: (categoryId: string, productId: string) => void
  deselectProduct: (categoryId: string, productId: string) => void
  answerQuestion: (questionId: string, answer: 'yes' | 'no') => void
  reset: () => void

  // Computed (getters)
  getSelectedProductIds: () => string[]
  getVisibleCategories: () => string[]
  getTotalPrice: () => number
  getConfigString: () => string
  isValid: () => boolean
}

export const useConfiguratorStore = create<ConfiguratorStore>((set, get) => ({
  // Initial data
  funnelId: null,
  size: 's',
  categories: [],
  configSteps: [],
  decisionTree: null,
  baseProductId: null,
  baseImage: null,

  // Initial state
  selections: [],
  questionAnswers: {},

  // Initialize with funnel data
  init: (data) => {
    set({
      funnelId: data.funnelId,
      size: data.size,
      categories: data.categories,
      configSteps: data.configSteps,
      decisionTree: data.decisionTree || null,
      baseProductId: data.baseProductId,
      baseImage: data.baseImage || null,
      selections: [],
      questionAnswers: {},
    })
  },

  selectProduct: (categoryId, productId) => {
    const { categories } = get()
    const category = categories.find(c => c.id === categoryId)
    if (!category) return

    // Verify product is in category (handle both productIds array and products array)
    const productIds = category.productIds || (category as any).products?.map((p: { id: string }) => p.id) || []
    if (!productIds.includes(productId)) return

    set((state) => {
      const existing = state.selections.find(s => s.categoryId === categoryId)

      // Single select: replace any existing selection
      return {
        selections: existing
          ? state.selections.map(s =>
              s.categoryId === categoryId
                ? { ...s, productIds: [productId] }
                : s
            )
          : [...state.selections, { categoryId, productIds: [productId] }],
      }
    })
  },

  deselectProduct: (categoryId, productId) => {
    set((state) => ({
      selections: state.selections.map(s =>
        s.categoryId === categoryId
          ? { ...s, productIds: s.productIds.filter(id => id !== productId) }
          : s
      ).filter(s => s.productIds.length > 0),
    }))
  },

  answerQuestion: (questionId, answer) => {
    set((state) => {
      // Find the question in configSteps
      const question = state.configSteps.find(
        (s): s is QuestionStep => s.type === 'question' && s.id === questionId
      )
      if (!question) return state

      // Get categories to hide based on the opposite answer
      const categoriesToHide = answer === 'yes'
        ? question.optionNo.showCategories
        : question.optionYes.showCategories

      // Remove selections from hidden categories
      const newSelections = state.selections.filter(
        s => !categoriesToHide.includes(s.categoryId)
      )

      return {
        questionAnswers: { ...state.questionAnswers, [questionId]: answer },
        selections: newSelections,
      }
    })
  },

  reset: () => {
    set({
      selections: [],
      questionAnswers: {},
    })
  },

  // Computed getters
  getSelectedProductIds: () => {
    const { selections } = get()
    return selections.flatMap(s => s.productIds)
  },

  getVisibleCategories: () => {
    const { configSteps, questionAnswers } = get()
    const visibleCategoryIds: string[] = []

    configSteps.forEach(step => {
      if (step.type === 'category') {
        // Direct category - always visible
        visibleCategoryIds.push(step.categoryId)
      } else if (step.type === 'question') {
        // Question - show categories based on answer
        const answer = questionAnswers[step.id]
        if (answer === 'yes') {
          visibleCategoryIds.push(...step.optionYes.showCategories)
        } else if (answer === 'no') {
          visibleCategoryIds.push(...step.optionNo.showCategories)
        }
        // If no answer yet, don't show any categories from this question
      }
    })

    return visibleCategoryIds
  },

  getTotalPrice: () => {
    const productIds = get().getSelectedProductIds()
    const productStore = useProductStore.getState()

    return productIds.reduce((sum, id) => {
      const product = productStore.getProduct(id)
      return sum + (product?.price || 0)
    }, 0)
  },

  getConfigString: () => {
    return get().selections.flatMap(s => s.productIds).join('-')
  },

  isValid: () => {
    const { categories, selections } = get()
    const visibleCategoryIds = get().getVisibleCategories()
    const visibleRequired = categories.filter(
      c => visibleCategoryIds.includes(c.id) && c.required
    )

    return visibleRequired.every(cat => {
      const selection = selections.find(s => s.categoryId === cat.id)
      return selection && selection.productIds.length > 0
    })
  },
}))

// Helper hook to get selected products with full data
export function useSelectedProducts(): Product[] {
  const selectedIds = useConfiguratorStore(state => state.getSelectedProductIds())
  const products = useProductStore(state => state.products)

  return selectedIds
    .map(id => products.find(p => p.id === id))
    .filter((p): p is Product => p !== undefined)
}
